import type { ReactNode } from "react";
import { labelClass } from "./fieldStyles";

interface FieldShellProps {
  id: string;
  label: string;
  required?: boolean;
  hint?: string | null;
  hintId?: string;
  error?: string | null;
  errorId?: string;
  containerClassName?: string;
  /** Render the label as a plain span (for radiogroups and other composite controls). */
  labelAs?: "label" | "span";
  /** Keep the label for screen readers only (dense grid cells whose column header is the visible label). */
  hideLabel?: boolean;
  children: ReactNode;
}

/** Label, optional hint, control, and inline error in the shared field layout. */
export function FieldShell({ id, label, required, hint, hintId, error, errorId, containerClassName, labelAs = "label", hideLabel = false, children }: FieldShellProps) {
  const marker = required ? (
    <span className="text-danger" aria-hidden="true">
      &nbsp;*
    </span>
  ) : null;

  return (
    <div className={containerClassName}>
      {labelAs === "label" ? (
        <label htmlFor={id} className={hideLabel ? "sr-only" : labelClass()}>
          {label}
          {marker}
        </label>
      ) : (
        <span id={`${id}-label`} className={hideLabel ? "sr-only" : labelClass()}>
          {label}
          {marker}
        </span>
      )}
      {hint && (
        <p id={hintId} className="text-xs text-gray-500 mb-1.5">
          {hint}
        </p>
      )}
      {children}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
